import React from 'react';
import { motion } from 'framer-motion';
import { Pencil, Send, ArrowLeft, ClipboardCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FLOW, TRACKS, SECTION_ORDER } from '@/data/flow';
import { formatIDR } from '@/lib/storage';

const LABELS = {
  fullName: 'Nama lengkap', email: 'Email', phone: 'WhatsApp', city: 'Domisili',
  photo: 'Foto profil',
  social: 'Media sosial',
  education: 'Pendidikan saat ini / terakhir', track: 'Posisi dilamar', skills: 'Skill',
  positionKnowledge: 'Pengetahuan bidang', skillTest: 'Tes skill', experience: 'Pengalaman', portfolio: 'Portofolio',
  motivation: 'Motivasi', duration: 'Durasi magang', salary: 'Ekspektasi uang saku',
};

const SOCIAL_LABELS = { instagram: 'Instagram', linkedin: 'LinkedIn', github: 'GitHub', twitter: 'X' };

const PhotoThumb = ({ file }) => {
  // Not revoking on cleanup — StrictMode's double-invoke would revoke a URL
  // still referenced by the <img>, causing a broken image in the review.
  const url = React.useMemo(() => (file instanceof File ? URL.createObjectURL(file) : null), [file]);
  if (!url) return <span className="text-muted-foreground">Dilewati</span>;
  return (
    <div className="flex items-center gap-3">
      <img src={url} alt="Foto profil" className="h-14 w-14 rounded-lg object-cover ring-1 ring-border" />
      <span className="truncate text-sm text-foreground">{file.name}</span>
    </div>
  );
};

const renderValue = (key, value) => {
  if (key === 'photo') {
    return value instanceof File ? <PhotoThumb file={value} /> : <span className="text-muted-foreground">Dilewati</span>;
  }
  if (key === 'skills') {
    if (!value || value.length === 0) return <span className="text-muted-foreground">—</span>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {value.map((s) => (
          <Badge key={s} variant="secondary" className="rounded-full bg-primary-soft text-primary hover:bg-primary-soft">{s}</Badge>
        ))}
      </div>
    );
  }
  if (key === 'portfolio') {
    if (!value || value.length === 0) return <span className="text-muted-foreground">Dilewati</span>;
    return (
      <div className="space-y-1">
        {value.map((l) => (
          <a key={l} href={l} target="_blank" rel="noreferrer" className="block break-all text-primary hover:underline">{l}</a>
        ))}
      </div>
    );
  }
  if (key === 'social') {
    const entries = Object.entries(value || {}).filter(([, val]) => val && val.trim());
    if (entries.length === 0) return <span className="text-muted-foreground">Dilewati</span>;
    return (
      <div className="space-y-0.5">
        {entries.map(([k, val]) => (
          <div key={k} className="text-sm"><span className="text-muted-foreground">{SOCIAL_LABELS[k] || k}:</span> {val}</div>
        ))}
      </div>
    );
  }
  if (key === 'skillTest') {
    if (!value || value.length === 0) return <span className="text-muted-foreground">Dilewati</span>;
    return (
      <div className="space-y-2">
        {value.map((t, i) => (
          <div key={i}>
            <p className="text-xs font-medium text-foreground">{i + 1}. {t.question}</p>
            <p className="text-sm text-muted-foreground">{t.answer || '—'}</p>
          </div>
        ))}
      </div>
    );
  }
  if (key === 'track') return TRACKS.find((t) => t.value === value)?.label || '—';
  if (key === 'education') {
    if (!value || !value.level) return '—';
    return `${value.level} · ${value.major} · ${value.institution}${value.year ? ` (${value.year})` : ''}`;
  }
  if (key === 'salary') return value === null || value === undefined ? 'Fleksibel / sesuai kebijakan' : `${formatIDR(value)} / bulan`;
  if (!value || (typeof value === 'string' && value.trim() === '')) return <span className="text-muted-foreground">Dilewati</span>;
  return value;
};

export const ReviewPanel = ({
  answers, onEdit, onSubmit, onBack, submitLabel = 'Kirim Lamaran',
  submitting = false,
}) => {
  const canSubmit = !submitting;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full overflow-hidden rounded-3xl border border-border bg-card shadow-elegant"
    >
      <div className="flex items-center gap-3 border-b border-border px-6 py-5 sm:px-8">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary-soft text-primary">
          <ClipboardCheck className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display text-xl font-bold text-foreground">Cek lagi, ya</h2>
          <p className="text-sm text-muted-foreground">Pastikan semuanya sudah pas. Klik “Ubah” untuk merevisi bagian apa pun.</p>
        </div>
      </div>

      <div className="max-h-[58vh] space-y-6 overflow-y-auto scrollbar-slim p-6 sm:p-8">
        {SECTION_ORDER.map((section) => {
          const steps = FLOW.filter((s) => s.section === section);
          return (
            <div key={section}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary">{section}</p>
              <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border">
                {steps.map((step) => (
                  <div key={step.key} className="flex items-start gap-3 p-4 transition-colors hover:bg-secondary/30">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-muted-foreground">{LABELS[step.key]}</p>
                      <div className="mt-1 break-words text-sm text-foreground">{renderValue(step.key, answers[step.key])}</div>
                    </div>
                    <Button onClick={() => onEdit(step.key)} variant="ghost" size="sm" className="shrink-0 text-muted-foreground hover:bg-primary-soft hover:text-primary">
                      <Pencil className="h-3.5 w-3.5" /> Ubah
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-4 border-t border-border p-5 sm:px-8">
        <div className="flex items-center justify-between gap-3">
          <Button onClick={onBack} variant="hero" disabled={submitting}>
            <ArrowLeft className="h-4 w-4" /> Kembali
          </Button>
          <Button onClick={onSubmit} variant="brand" size="lg" disabled={!canSubmit}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Mengirim...
              </>
            ) : (
              <>
                {submitLabel} <Send className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
